import { IconClick } from "@tabler/icons-react";
import { useMemo } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { ANALYTICS_QUERY } from "../../constants";
import { useAnalyticsQuery } from "../../hooks";
import { Skeleton } from "./skeleton";
import { StageRow } from "./stage-row";

type EngagementStage = {
  opened: number;
  started_reading: number;
  finished_reading: number;
};

type StageKey = keyof EngagementStage;

type StageConfig = {
  label: string;
  key: StageKey;
};

type StageViewModel = StageConfig & {
  total: number;
  percentage: number;
};

export const EngagementFunnel = ({
  overrides,
}: {
  overrides?: Record<string, unknown> | undefined;
}) => {
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });
  const {
    data: response,
    isLoading,
    isError,
  } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.ENGAGEMENT_FUNNEL],
    overrides: overrides ?? {},
    enabled: isIntersecting,
  });

  const data = response?.[ANALYTICS_QUERY.ENGAGEMENT_FUNNEL] as
    | EngagementStage
    | undefined;

  const opened = Number(data?.opened) || 0;
  const startedReading = Number(data?.started_reading) || 0;
  const finishedReading = Number(data?.finished_reading) || 0;

  const stagesData: StageViewModel[] = useMemo(() => {
    const max = opened;
    const percentage = (value: number) =>
      max > 0 ? Math.round((value / max) * 100) : 0;

    return [
      {
        label: "Opened Story",
        key: "opened",
        total: opened,
        percentage: percentage(opened),
      },
      {
        label: "Started scrolling",
        key: "started_reading",
        total: startedReading,
        percentage: percentage(startedReading),
      },
      {
        label: "Completed reading",
        key: "finished_reading",
        total: finishedReading,
        percentage: percentage(finishedReading),
      },
    ];
  }, [opened, startedReading, finishedReading]);

  return (
    <div
      ref={ref}
      className="tw-flex tw-flex-col tw-gap-4 tw-p-5 tw-bg-white tw-outline tw-outline-1 tw-outline-blue/5 tw-rounded-xl tw-shadow-2xs"
    >
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <IconClick className="tw-size-4" />
        <h2 className="tw-font-sans tw-text-base tw-font-bold tw-text-blue">
          Engagement funnel
        </h2>
      </div>
      {isLoading ? (
        <Skeleton />
      ) : (
        <div className="tw-grid tw-grid-cols-[1fr_auto] tw-gap-x-6 tw-gap-y-2 tw-items-center">
          {stagesData.map((stage, index) => (
            <StageRow
              key={stage.key}
              label={stage.label}
              percentage={stage.percentage}
              total={stage.total}
              isError={isError}
              isLast={index === stagesData.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};
